import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { formatForAPI } from '../../../../utils/dateformatpickr';
import {
  DateField,
  FileField,
  FormGrid,
  RequiredNote,
  TextField,
  focusFirstError,
  hasErrors,
  required,
  validateForm,
} from '../../../../Components/Form';
import ActionButton from '../../../../Components/Common/ActionButton';
import { CRUDE_DOC_ACCEPT } from './registrationConstants';

// Shared "Add Document" popup used by both the Evaluation tab and the
// Implementation tab (rule-crude-registration.md §Tab Evaluation 2.1 / §Tab
// Implementation b.1). Collects Reference Document (text), Effective Date
// (date picker) and a file (PDF), then hands them to the parent's `onSave`.
// The parent only STAGES the entry locally (no network call here) — the actual
// upload is batch-flushed later at the tab boundary — so `onSave` is synchronous
// and this component stays presentational and reusable.
const VALIDATION = {
  refDocument: { label: 'Reference document', rules: [required()] },
  effDate: { label: 'Effective date', rules: [required()] },
  file: { label: 'File', rules: [required()] },
};
const FIELD_ORDER = ['refDocument', 'effDate', 'file'];

const AddDocumentModal = ({ isOpen, toggle, onSave }) => {
  const [refDocument, setRefDocument] = useState('');
  const [effDate, setEffDate] = useState(null);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);

  const reset = () => {
    setRefDocument('');
    setEffDate(null);
    setFile(null);
    setErrors({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Clear the form whenever the modal is (re)opened so a previous entry doesn't
  // leak into the next section's Add.
  useEffect(() => {
    if (isOpen) reset();
  }, [isOpen]);

  const handleFileChange = e => {
    const f = e.target.files[0] || null;
    // Draft document flow accepts .pdf and .cru (api-crude-registration.md §D4).
    if (f && !/\.(pdf|cru)$/i.test(f.name)) {
      setFile(null);
      setErrors(prev => ({
        ...prev,
        file: 'Only .pdf or .cru files are allowed',
      }));
      return;
    }
    setFile(f);
    setErrors(prev => ({ ...prev, file: '' }));
  };

  const handleSave = () => {
    const newErrors = validateForm({ refDocument, effDate, file }, VALIDATION);
    setErrors(newErrors);
    if (hasErrors(newErrors)) {
      focusFirstError(newErrors, FIELD_ORDER);
      return;
    }

    onSave({
      refDocument: refDocument.trim(),
      effDate: formatForAPI(effDate),
      file,
    });
  };

  const handleCancel = () => {
    reset();
    toggle();
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleCancel}
      centered
      data-bs-backdrop='static'
    >
      <ModalHeader toggle={handleCancel}>Add Document</ModalHeader>
      <ModalBody>
        <RequiredNote />
        <FormGrid columns={1}>
          <TextField
            label='Reference document'
            name='refDocument'
            required
            placeholder='e.g. Q88 questionnaire'
            value={refDocument}
            error={errors.refDocument}
            onChange={e => {
              setRefDocument(e.target.value);
              setErrors(prev => ({ ...prev, refDocument: '' }));
            }}
          />
          <DateField
            label='Effective date'
            name='effDate'
            required
            value={effDate}
            error={errors.effDate}
            onChange={date => {
              setEffDate(date);
              setErrors(prev => ({ ...prev, effDate: '' }));
            }}
          />
          <FileField
            label='File'
            name='file'
            required
            accept={CRUDE_DOC_ACCEPT}
            inputRef={fileInputRef}
            hint='Supported formats: .pdf, .cru'
            error={errors.file}
            onChange={handleFileChange}
          />
        </FormGrid>
      </ModalBody>
      <ModalFooter>
        <ActionButton
          variant='secondary'
          icon='ri-close-line'
          onClick={handleCancel}
        >
          Cancel
        </ActionButton>
        <ActionButton
          variant='primary'
          icon='ri-save-line'
          onClick={handleSave}
        >
          Save
        </ActionButton>
      </ModalFooter>
    </Modal>
  );
};

AddDocumentModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  onSave: PropTypes.func,
};

export default AddDocumentModal;
